import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Typography,
  Chip,
  Button,
  Collapse,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  CircularProgress,
  Alert,
} from "@mui/material";
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Person as PersonIcon,
  LocalHospital as DoctorIcon,
  Medication as MedicineIcon,
  FilterList as FilterIcon,
} from "@mui/icons-material";
import { usePharmacySearch } from "../../hooks/usePharmacySearch";

/**
 * Enhanced Pharmacy Search Component
 * Provides advanced search functionality with suggestions, filters, and real-time results
 */
const PharmacySearchComponent = ({
  onSearchResults,
  searchType = "all", // 'all', 'pos', 'prescription', 'free_medicine'
  placeholder = "Search pharmacy records...",
  showFilters = true,
  showSuggestions = true,
  autoSearch = true,
  debounceDelay = 300,
  className = "",
  sx = {},
}) => {
  const [query, setQuery] = useState("");
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [filters, setFilters] = useState({
    payment_status: "",
    date_from: "",
    date_to: "",
    patient_id: "",
    doctor_id: "",
  });
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
  });

  const {
    searchResults,
    isLoading,
    error,
    suggestions,
    search,
    quickSearch,
    clearResults,
    clearSuggestions,
  } = usePharmacySearch();

  const searchTimeoutRef = useRef(null);
  const inputRef = useRef(null);

  // Debounced search effect
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (query && autoSearch) {
      searchTimeoutRef.current = setTimeout(() => {
        performSearch();
      }, debounceDelay);
    } else if (!query && autoSearch) {
      clearResults();
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [query, filters, pagination, autoSearch, debounceDelay]);

  // Quick search for suggestions
  useEffect(() => {
    if (query && query.length >= 2 && showSuggestions) {
      quickSearch(query);
    } else {
      clearSuggestions();
    }
  }, [query, quickSearch, showSuggestions]);

  // Notify parent component of search results
  useEffect(() => {
    if (onSearchResults) {
      onSearchResults(searchResults);
    }
  }, [searchResults, onSearchResults]);

  const performSearch = async () => {
    try {
      await search({
        search: query,
        type: searchType,
        ...filters,
        ...pagination,
      });
    } catch (err) {
      console.error("Search failed:", err);
    }
  };

  const handleQueryChange = (event) => {
    setQuery(event.target.value);
  };

  const handleFilterChange = (filterName, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterName]: value,
    }));
    setPagination((prev) => ({
      ...prev,
      page: 1, // Reset to first page when filters change
    }));
  };

  const handleClearSearch = () => {
    setQuery("");
    clearResults();
    clearSuggestions();
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleClearFilters = () => {
    setFilters({
      payment_status: "",
      date_from: "",
      date_to: "",
      patient_id: "",
      doctor_id: "",
    });
  };

  const handleSuggestionClick = (suggestion) => {
    if (suggestion.type === "patient") {
      handleFilterChange("patient_id", suggestion.value);
    } else if (suggestion.type === "doctor") {
      handleFilterChange("doctor_id", suggestion.value);
    } else if (suggestion.type === "medicine") {
      setQuery(suggestion.text);
    }
    clearSuggestions();
  };

  const handleManualSearch = () => {
    performSearch();
  };

  const getSuggestionIcon = (type) => {
    switch (type) {
      case "patient":
        return <PersonIcon />;
      case "doctor":
        return <DoctorIcon />;
      case "medicine":
        return <MedicineIcon />;
      default:
        return <SearchIcon />;
    }
  };

  const getSuggestionColor = (type) => {
    switch (type) {
      case "patient":
        return "primary";
      case "doctor":
        return "secondary";
      case "medicine":
        return "success";
      default:
        return "default";
    }
  };

  return (
    <Box className={className} sx={sx}>
      {/* Main Search Input */}
      <Box sx={{ position: "relative" }}>
        <TextField
          ref={inputRef}
          fullWidth
          variant="outlined"
          placeholder={placeholder}
          value={query}
          onChange={handleQueryChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                {isLoading && <CircularProgress size={20} />}
                {query && (
                  <IconButton onClick={handleClearSearch} size="small">
                    <ClearIcon />
                  </IconButton>
                )}
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
            },
          }}
        />

        {/* Suggestions Dropdown */}
        {showSuggestions && suggestions.length > 0 && (
          <Paper
            sx={{
              position: "absolute",
              top: "100%",
              left: 0,
              right: 0,
              zIndex: 1000,
              mt: 1,
              maxHeight: 300,
              overflow: "auto",
              boxShadow: 3,
            }}
          >
            <List dense>
              {suggestions.map((suggestion, index) => (
                <ListItem
                  key={index}
                  button
                  onClick={() => handleSuggestionClick(suggestion)}
                  sx={{
                    "&:hover": {
                      backgroundColor: "action.hover",
                    },
                  }}
                >
                  <ListItemIcon>
                    {getSuggestionIcon(suggestion.type)}
                  </ListItemIcon>
                  <ListItemText
                    primary={suggestion.text}
                    secondary={
                      <Chip
                        label={suggestion.type}
                        size="small"
                        color={getSuggestionColor(suggestion.type)}
                        variant="outlined"
                      />
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        )}
      </Box>

      {/* Advanced Filters */}
      {showFilters && (
        <>
          <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<FilterIcon />}
              endIcon={
                showAdvancedFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />
              }
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              size="small"
            >
              Filters
            </Button>

            {(filters.payment_status ||
              filters.date_from ||
              filters.date_to ||
              filters.patient_id ||
              filters.doctor_id) && (
              <Button
                variant="text"
                onClick={handleClearFilters}
                size="small"
                color="secondary"
              >
                Clear Filters
              </Button>
            )}
          </Box>

          <Collapse in={showAdvancedFilters}>
            <Paper sx={{ p: 2, mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Payment Status</InputLabel>
                    <Select
                      value={filters.payment_status}
                      onChange={(e) =>
                        handleFilterChange("payment_status", e.target.value)
                      }
                      label="Payment Status"
                    >
                      <MenuItem value="">All Statuses</MenuItem>
                      <MenuItem value="1">Pending</MenuItem>
                      <MenuItem value="2">Paid</MenuItem>
                      <MenuItem value="3">Canceled</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    label="From Date"
                    value={filters.date_from}
                    onChange={(e) =>
                      handleFilterChange("date_from", e.target.value)
                    }
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    label="To Date"
                    value={filters.date_to}
                    onChange={(e) =>
                      handleFilterChange("date_to", e.target.value)
                    }
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      alignItems: "center",
                      height: "100%",
                    }}
                  >
                    <Button
                      variant="contained"
                      onClick={handleManualSearch}
                      disabled={isLoading}
                      sx={{ flex: 1 }}
                    >
                      {isLoading ? <CircularProgress size={20} /> : "Search"}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Collapse>
        </>
      )}

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      {/* Search Results Summary */}
      {searchResults.total_count > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Found {searchResults.total_count} results
            {searchResults.pos_count > 0 &&
              ` (${searchResults.pos_count} POS orders)`}
            {searchResults.prescription_count > 0 &&
              ` (${searchResults.prescription_count} prescriptions)`}
            {searchResults.free_medicine_count > 0 &&
              ` (${searchResults.free_medicine_count} free medicine orders)`}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default PharmacySearchComponent;
